/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { alpha, type Theme, ToggleButton } from '@mui/material';
import type { ReactNode } from 'react';
import ToggleButtonGroup from '../../../react-components/inputs/ToggleButtonGroup';

export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
  icon?: ReactNode;
}

const groupSx = (theme: Theme) => ({
  p: '3px',
  gap: '2px',
  borderRadius: '9px',
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: alpha(theme.palette.background.default, 0.6),
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid transparent',
    borderRadius: '7px !important',
    m: 0,
    px: 1.25,
    py: 0.5,
    gap: 0.75,
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
    color: 'text.secondary',
    '& svg': { fontSize: 15 },
    '&.Mui-selected': {
      color: 'text.primary',
      fontWeight: 600,
      bgcolor: 'action.selected',
      borderColor: theme.palette.divider,
    },
  },
});

/** Pill-style single choice used across settings and the user menu. */
export const SegmentedControl = <T extends string | number>({
  value,
  options,
  onChange,
  fullWidth = false,
  ...aria
}: Readonly<{
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  fullWidth?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}>) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    fullWidth={fullWidth}
    value={value}
    onChange={(_, next: T | null) => {
      if (next !== null) onChange(next);
    }}
    sx={groupSx}
    {...aria}
  >
    {options.map((option) => (
      <ToggleButton key={option.value} value={option.value}>
        {option.icon}
        {option.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);
